import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Receipt, ArrowDownCircle, ArrowUpCircle, Search, Trash2, Printer, FileText, Calendar, Plus, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SupplierPaymentDialog from '../components/SupplierPaymentDialog';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtSGD = (n) => `S$${Number(n || 0).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const methodColor = (m) => ({
  'Cash': 'bg-emerald-100 text-emerald-700',
  'PayNow': 'bg-purple-100 text-purple-700',
  'Cheque': 'bg-amber-100 text-amber-700',
  'Bank Transfer': 'bg-blue-100 text-blue-700',
}[m] || 'bg-gray-100 text-gray-700');

const Payments = () => {
  const [typeFilter, setTypeFilter] = useState('all');   // all | receipt | supplier
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [receipts, setReceipts] = useState([]);
  const [supplierPays, setSupplierPays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSupplierPayModal, setShowSupplierPayModal] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (methodFilter !== 'all') params.payment_method = methodFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [r1, r2] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/payment-vouchers`, { params }),
        axios.get(`${BACKEND_URL}/api/supplier-payments`, { params }),
      ]);
      setReceipts(r1.data || []);
      setSupplierPays(r2.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load payments'));
    } finally {
      setLoading(false);
    }
  }, [methodFilter, dateFrom, dateTo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const removeItem = async (item) => {
    const endpoint = item._type === 'receipt' ? 'payment-vouchers' : 'supplier-payments';
    const note = item._type === 'receipt' ? ' Linked invoice will revert to unpaid.' : '';
    if (!window.confirm(`Delete ${item.document_number}?${note}`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/${endpoint}/${item.id}`);
      toast.success('Deleted');
      fetchAll();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to delete')); }
  };

  const sendViaWhatsApp = (item) => {
    const isReceipt = item._type === 'receipt';
    const phone = isReceipt ? (item.customer_phone || '') : (item.supplier_phone || '');
    if (!phone) {
      toast.error(`No phone number on file for this ${isReceipt ? 'customer' : 'supplier'}. Add a phone number first.`);
      return;
    }
    const endpoint = isReceipt ? 'payment-vouchers' : 'supplier-payments';
    const docLabel = isReceipt ? 'Official Receipt' : 'Payment Voucher';
    const pdfUrl = `${BACKEND_URL}/api/${endpoint}/${item.id}/pdf`;
    const greeting = isReceipt
      ? `Hi ${item.customer_name},\n\nThank you for your payment. Please find attached your ${docLabel} ${item.document_number} for S$${Number(item.amount).toFixed(2)} dated ${item.payment_date}.`
      : `Hi ${item.supplier_name},\n\nPlease find attached ${docLabel} ${item.document_number} confirming payment of S$${Number(item.amount).toFixed(2)} on ${item.payment_date}${item.reference ? ` (Ref: ${item.reference})` : ''}.`;
    const msg = `${greeting}\n\n📎 Download: ${pdfUrl}\n\nThank you,\nNJK Logistics Pte Ltd\n+65 9836 6186`;
    let p = phone.replace(/\D/g, '');
    if (p.length === 8) p = '65' + p;
    const url = `https://wa.me/${p}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
    toast.success(`Opening WhatsApp for ${isReceipt ? item.customer_name : item.supplier_name}`);
  };

  // Tag each item with type for unified display
  const taggedReceipts = receipts.map(r => ({ ...r, _type: 'receipt' }));
  const taggedSupplier = supplierPays.map(r => ({ ...r, _type: 'supplier' }));

  // Combine + filter
  let combined = [];
  if (typeFilter === 'receipt') combined = taggedReceipts;
  else if (typeFilter === 'supplier') combined = taggedSupplier;
  else combined = [...taggedReceipts, ...taggedSupplier];

  // Sort by payment_date desc
  combined.sort((a, b) => (b.payment_date || '').localeCompare(a.payment_date || ''));

  const q = search.toLowerCase();
  const filtered = combined.filter(p => {
    if (!q) return true;
    return (
      p.document_number?.toLowerCase().includes(q) ||
      (p.customer_name || p.supplier_name || '')?.toLowerCase().includes(q) ||
      p.invoice_number?.toLowerCase().includes(q) ||
      p.reference?.toLowerCase().includes(q)
    );
  });

  const totalIn = taggedReceipts.reduce((s, p) => s + (p.amount || 0), 0);
  const totalOut = taggedSupplier.reduce((s, p) => s + (p.amount || 0), 0);
  const net = totalIn - totalOut;

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin"><Button variant="outline" data-testid="back-btn"><ArrowLeft size={18} /></Button></Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Receipt size={28} className="text-emerald-600" /> Payments
            </h1>
            <p className="text-gray-600 text-sm mt-1">All receipts (money in) and payment vouchers (money out) — in one place</p>
          </div>
          <Button onClick={() => setShowSupplierPayModal(true)}
                  className="bg-rose-600 hover:bg-rose-700"
                  data-testid="add-supplier-payment-btn">
            <Plus size={16} className="mr-1" /> Record Supplier Payment
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <Card className="border-emerald-200">
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1">
                <ArrowDownCircle size={11} /> Money In (Customers)
              </div>
              <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="total-in">{fmtSGD(totalIn)}</div>
              <p className="text-[10px] text-gray-500 mt-1">{taggedReceipts.length} receipts</p>
            </CardContent>
          </Card>
          <Card className="border-rose-200">
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold flex items-center gap-1">
                <ArrowUpCircle size={11} /> Money Out (Suppliers)
              </div>
              <div className="text-2xl font-bold text-rose-700 mt-1" data-testid="total-out">{fmtSGD(totalOut)}</div>
              <p className="text-[10px] text-gray-500 mt-1">{taggedSupplier.length} vouchers</p>
            </CardContent>
          </Card>
          <Card className={net >= 0 ? 'border-indigo-200 bg-indigo-50/30' : 'border-orange-200 bg-orange-50/30'}>
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-indigo-700">Net Cash Flow</div>
              <div className={`text-2xl font-bold mt-1 ${net >= 0 ? 'text-indigo-700' : 'text-orange-700'}`} data-testid="net-flow">
                {net >= 0 ? '+' : ''}{fmtSGD(net)}
              </div>
              <p className="text-[10px] text-gray-500 mt-1">{net >= 0 ? 'Positive — profit on collected' : 'Negative — paid more than collected'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div>
                <Label className="text-xs">Show</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="h-9" data-testid="type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Documents</SelectItem>
                    <SelectItem value="receipt">📥 Customer Receipts (OR)</SelectItem>
                    <SelectItem value="supplier">📤 Supplier Payments (PV)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input placeholder="No., name, invoice ref..."
                         value={search} onChange={(e) => setSearch(e.target.value)}
                         className="pl-9 h-9" data-testid="search" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Method</Label>
                <Select value={methodFilter} onValueChange={setMethodFilter}>
                  <SelectTrigger className="h-9" data-testid="method-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Methods</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="PayNow">PayNow</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Date range</Label>
                <div className="flex gap-1">
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" data-testid="from" />
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" data-testid="to" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12 text-gray-500">
              <Receipt size={36} className="mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No payment records yet</p>
              <p className="text-sm mt-1">
                Open an Invoice → <em>Record Customer Payment</em> for a receipt, or click
                <em> Record Supplier Payment</em> above for a voucher.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="payments-list">
            {filtered.map((p) => {
              const isReceipt = p._type === 'receipt';
              const name = isReceipt ? p.customer_name : p.supplier_name;
              const refExtra = isReceipt ? p.invoice_number : p.reference;
              const endpoint = isReceipt ? 'payment-vouchers' : 'supplier-payments';
              const color = isReceipt ? 'emerald' : 'rose';
              const Icon = isReceipt ? ArrowDownCircle : ArrowUpCircle;
              const typeLabel = isReceipt ? 'Receipt (IN)' : 'Payment (OUT)';
              return (
                <Card key={`${p._type}-${p.id}`} className="hover:shadow-md transition-shadow"
                      data-testid={`payment-row-${p.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge className={`text-[10px] bg-${color}-100 text-${color}-700 hover:bg-${color}-100`}>
                            <Icon size={11} className="mr-1" /> {typeLabel}
                          </Badge>
                          <h3 className="font-bold text-gray-900 font-mono">{p.document_number}</h3>
                          <Badge className={`text-[10px] ${methodColor(p.payment_method)} hover:${methodColor(p.payment_method)}`}>
                            {p.payment_method}
                          </Badge>
                          {refExtra && (
                            <Badge variant="outline" className="text-[10px]">
                              <FileText size={10} className="mr-1" /> {refExtra}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-semibold text-gray-800">{name}</p>
                        <div className="text-xs text-gray-600 flex flex-wrap gap-3 mt-1">
                          <span className="flex items-center gap-1"><Calendar size={11} /> {p.payment_date}</span>
                          {p.received_by && <span>Received by: {p.received_by}</span>}
                          {p.authorised_by && <span>Authorised by: {p.authorised_by}</span>}
                          {p.cheque_no && <span>Cheque: {p.cheque_no}</span>}
                        </div>
                        {p.description && <p className="text-xs text-gray-500 mt-1 truncate">{p.description}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className={`text-xl font-extrabold ${isReceipt ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {isReceipt ? '+' : '−'}{fmtSGD(p.amount)}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" className="h-8 px-2 bg-emerald-500 hover:bg-emerald-600 text-white"
                                  onClick={() => sendViaWhatsApp(p)} title="Send via WhatsApp"
                                  data-testid={`wa-${p.id}`}>
                            <MessageCircle size={14} />
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 px-2"
                                  onClick={() => window.open(`${BACKEND_URL}/api/${endpoint}/${p.id}/pdf`, '_blank')}
                                  title="Print / Download PDF">
                            <Printer size={14} />
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 px-2 text-red-600 hover:bg-red-50"
                                  onClick={() => removeItem(p)} title="Delete">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <SupplierPaymentDialog
          open={showSupplierPayModal}
          onOpenChange={setShowSupplierPayModal}
          onCreated={() => fetchAll()}
        />
      </div>
    </div>
  );
};

export default Payments;
